import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Production domain - canonical URLs should always point here
const PRODUCTION_DOMAIN = 'https://globalvyapar.co.in';

/**
 * SEO Component for dynamic meta tags
 * Updates document title and meta description for each page
 */
const SEO = ({ 
  title, 
  description, 
  keywords = '',
  canonical = '',
  type = 'website',
  noindex = false
}) => {
  const location = useLocation();
  
  useEffect(() => {
    // Update document title
    const fullTitle = title 
      ? `${title} | GlobalVyapar` 
      : 'GlobalVyapar - AI-Powered Global B2B Trade Platform';
    document.title = fullTitle;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = description || 'GlobalVyapar — AI-powered B2B marketplace connecting verified exporters with global buyers. Browse trusted suppliers across all product categories worldwide.';

    // Update keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    if (keywords) {
      metaKeywords.content = keywords;
    }

    // Update canonical URL - ALWAYS use production domain
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    // Use provided canonical or build from current path with production domain
    const canonicalUrl = canonical || `${PRODUCTION_DOMAIN}${location.pathname}`;
    canonicalLink.href = canonicalUrl;

    // Update robots meta tag for pages that shouldn't be indexed
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = noindex ? 'noindex, nofollow' : 'index, follow';

    // Update Open Graph tags
    const updateOGTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    updateOGTag('og:title', fullTitle);
    updateOGTag('og:description', description || 'Connect with verified exporters worldwide on GlobalVyapar.');
    updateOGTag('og:type', type);
    updateOGTag('og:url', canonicalUrl);
    updateOGTag('og:site_name', 'GlobalVyapar');
    updateOGTag('og:locale', 'en_US');

    // Update Twitter tags
    const updateTwitterTag = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    updateTwitterTag('twitter:card', 'summary_large_image');
    updateTwitterTag('twitter:title', fullTitle);
    updateTwitterTag('twitter:description', description || 'Connect with verified exporters worldwide.');

  }, [title, description, keywords, canonical, type, noindex, location.pathname]);

  return null; // This component doesn't render anything
};

// Pre-defined SEO configs for common pages
export const SEO_CONFIGS = {
  home: {
    title: null, // Will use default
    description: 'GlobalVyapar — AI-powered B2B marketplace connecting verified exporters with global buyers. Browse trusted suppliers across all product categories worldwide.',
    keywords: 'B2B marketplace, global exporters, international trade, verified suppliers, export import, trade platform'
  },
  marketplace: {
    title: 'Marketplace - Browse Products',
    description: 'Browse thousands of verified products from trusted exporters worldwide. Spices, grains, textiles, and more. Business verified sellers.',
    keywords: 'wholesale products, export products, global suppliers, verified sellers, B2B marketplace'
  },
  auth: {
    title: 'Login / Register',
    description: 'Join Global Vyapar - Register as a buyer or seller. Get verified and start trading with trusted partners worldwide.',
    keywords: 'register exporter, join B2B marketplace, seller registration, buyer signup'
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Manage your Global Vyapar account. Track leads, manage products, and grow your export business.',
    keywords: 'seller dashboard, export management, trade dashboard'
  },
  products: {
    title: 'My Products',
    description: 'Manage your product listings on Global Vyapar. Add products, update prices, and reach global buyers.',
    keywords: 'product listings, export products, manage inventory'
  },
  about: {
    title: 'About Us',
    description: 'Learn about GlobalVyapar - AI-powered B2B trade platform helping exporters connect with global buyers.',
    keywords: 'about global vyapar, B2B platform, global export platform'
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with Global Vyapar team. Support for buyers and sellers on our B2B trade platform.',
    keywords: 'contact global vyapar, B2B support, trade platform help'
  },
  privacyPolicy: {
    title: 'Privacy Policy',
    description: 'Global Vyapar privacy policy. Learn how we protect your data and privacy on our B2B trade platform.',
    keywords: 'privacy policy, data protection, B2B platform privacy'
  },
  termsOfService: {
    title: 'Terms of Service',
    description: 'Global Vyapar terms of service. Rules and guidelines for using our B2B trade platform.',
    keywords: 'terms of service, user agreement, B2B platform terms'
  }
};

// Helper to generate product page SEO
export const getProductSEO = (product) => ({
  title: `${product.title} - ${product.seller_name || 'Verified Seller'}`,
  description: `${product.title} from ${product.seller_name || 'verified seller'} in ${product.specifications?.location || product.origin_country || 'Worldwide'}. ${product.description?.substring(0, 120) || ''} MOQ: ${product.min_order} ${product.unit}. Price: $${product.price}/${product.unit}.`,
  keywords: `${product.title}, ${product.category}, verified exporter, ${product.origin_country || ''} supplier, wholesale`,
  type: 'product'
});

// Helper to generate category page SEO
export const getCategorySEO = (category) => ({
  title: `${category} - Verified Suppliers`,
  description: `Browse verified ${category} suppliers and exporters from India. Quality products, competitive prices, GSTIN & IEC verified sellers on Global Vyapar.`,
  keywords: `${category} suppliers, ${category} exporters India, wholesale ${category}, B2B ${category}`
});

// Helper to generate seller profile SEO
export const getSellerSEO = (seller) => ({
  title: `${seller.company_name || seller.full_name} - Verified Exporter`,
  description: `${seller.company_name || seller.full_name} - Verified exporter from ${seller.district || seller.city || 'India'}. ${seller.is_verified ? 'GSTIN & IEC Verified.' : ''} Browse products and connect on Global Vyapar.`,
  keywords: `${seller.company_name}, Indian exporter, verified supplier, ${seller.district || ''} exporter`,
  type: 'profile'
});

export default SEO;
